import { library } from "../data/aiWorkflows";
import { getGrowthJourneyContext, getJourneyId, JOURNEY_ID_PATTERN } from "./growthJourney";

export const PROMPT_LIBRARY_SOURCE = "fortune-100-prompt-library";
export const LIBRARY_CTA_PLACEMENTS = [
  "navigation",
  "hero",
  "starter",
  "workflow",
  "footer",
] as const;
export type LibraryCTAPlacement = (typeof LIBRARY_CTA_PLACEMENTS)[number];
const workflows = new Map(library.map((entry) => [entry.slug, entry.title]));
// Retain public buyer/campaign context through catalog and workflow links.
export function promptLibraryCampaignQuery(search: string) {
  const params = new URLSearchParams(getGrowthJourneyContext(search));
  return params.size ? `?${params}` : "";
}

export function getPromptLibraryContext(search: string) {
  const params = new URLSearchParams(search);
  if (
    params.get("source") !== PROMPT_LIBRARY_SOURCE &&
    params.get("library_source") !== PROMPT_LIBRARY_SOURCE
  )
    return null;
  const placement = params.get("library_location") as LibraryCTAPlacement;
  if (!LIBRARY_CTA_PLACEMENTS.includes(placement)) return null;
  const slug = params.get("workflow") || "library";
  if (slug !== "library" && !workflows.has(slug)) return null;
  return {
    source_page: PROMPT_LIBRARY_SOURCE,
    library_location: placement,
    workflow_slug: slug,
    workflow_title: workflows.get(slug),
  };
}

export function buildPromptLibraryAI30Url(
  search: string,
  placement: LibraryCTAPlacement,
  slug?: string,
) {
  const params = new URLSearchParams({
    source: PROMPT_LIBRARY_SOURCE,
    library_location: placement,
  });
  if (slug && workflows.has(slug)) params.set("workflow", slug);
  for (const [key, value] of Object.entries(getGrowthJourneyContext(search)))
    params.set(key, value);
  params.set("source", PROMPT_LIBRARY_SOURCE);
  params.set("library_location", placement);
  if (slug && workflows.has(slug)) params.set("workflow", slug);
  params.set("audience", "organization");
  return `/ai-30?${params}`;
}

export function buildPromptLibraryOfferUrl(search: string, placement: LibraryCTAPlacement, slug?: string) {
  return buildPromptLibraryAI30Url(search, placement, slug);
}

// Public catalog identifiers only. Stripe stores this on the Checkout Session;
// it describes attribution and never serves as proof of payment.
export function libraryCheckoutReference(search: string) {
  const context = getPromptLibraryContext(search);
  const id = getJourneyId(search);
  if (id) return `j1_${id}${context ? `__pl_${context.library_location}__${context.workflow_slug}` : ""}`;
  return context ? `pl1_${context.library_location}__${context.workflow_slug}` : null;
}

export function parseLibraryCheckoutReference(reference: unknown) {
  if (typeof reference !== "string") return null;
  const normalized = reference.replace(/^j1_[0-9a-f-]{36}__pl_/i, "pl1_");
  if (reference.startsWith("j1_") && !parseJourneyCheckoutReference(reference)) return null;
  const match = /^pl1_(navigation|hero|starter|workflow|footer)__([a-z0-9-]+)$/.exec(
    normalized,
  );
  if (!match || (match[2] !== "library" && !workflows.has(match[2])))
    return null;
  return { placement: match[1], workflow: match[2] };
}

// This opaque key joins provider receipts to a prior browser/intake journey.
// It never means that the session was paid or a calendar booking succeeded.
export function parseJourneyCheckoutReference(reference: unknown): { journey_id: string } | null {
  if (typeof reference !== "string") return null;
  const match = /^j1_([0-9a-f-]{36})(?:__pl_(navigation|hero|starter|workflow|footer)__([a-z0-9-]+))?$/i.exec(reference);
  if (!match || !JOURNEY_ID_PATTERN.test(match[1]) || (match[3] && match[3] !== "library" && !workflows.has(match[3]))) return null;
  return { journey_id: match[1].toLowerCase() };
}
